/**
 * gtag adapter — wraps window.gtag so every gtag('event', name, params) call is
 * observed before delegating to the original.
 *
 * Only useful on sites that call gtag() by hand. On GTM-managed sites the GA4
 * templates call into the gtag.js runtime internally and never touch
 * window.gtag — measured 0 calls on logo.com.tr — so auto-detection treats a
 * container-only page as "gtag unavailable".
 */

/**
 * @param {IArguments|Array} args
 * @returns {{name: string, params: object, userProps: object}|null}
 */
export function readGtagCall(args) {
  const list = Array.prototype.slice.call(args || []);
  if (list[0] !== 'event' || typeof list[1] !== 'string' || !list[1]) return null;
  const params = {};
  const raw = list[2];
  if (raw && typeof raw === 'object') {
    Object.keys(raw).forEach((key) => {
      const value = raw[key];
      if (value === undefined || value === null || typeof value === 'function') return;
      params[key] = value;
    });
  }
  return { name: list[1], params, userProps: {} };
}

/**
 * True when window.gtag looks like a real gtag function we can wrap.
 * @param {object} win
 * @returns {boolean}
 */
export function isGtagAvailable(win) {
  return !!(win && typeof win.gtag === 'function');
}

/**
 * @param {object} ctx { emit, log, win }
 * @returns {{ name: string, uninstall: Function }|null}
 */
export function installGtagAdapter(ctx) {
  const win = ctx.win;
  if (!isGtagAvailable(win)) return null;
  if (win.gtag.__b2m_ga_bridge) return { name: 'gtag', uninstall() {} };

  const original = win.gtag;
  const patched = function (...args) {
    try {
      ctx.emit(readGtagCall(args), 'gtag');
    } catch (e) {
      ctx.log('[B2M GA bridge] gtag call skipped', e);
    }
    // Delegate untouched and hand back GA's own return value.
    return original.apply(this, args);
  };
  patched.__b2m_ga_bridge = true;
  patched.__b2m_original = original;
  win.gtag = patched;

  ctx.log('[B2M GA bridge] gtag wrapper installed');

  return {
    name: 'gtag',
    uninstall() {
      if (win.gtag && win.gtag.__b2m_ga_bridge) win.gtag = win.gtag.__b2m_original;
    }
  };
}
